import {
  Landmark,
  ClipboardList,
  TrendingUp,
  LayoutDashboard,
  CreditCard,
  Layers,
  Home,
  type LucideIcon,
} from "lucide-react";
import { WELCOME_STEPS } from "@/content/guidance/welcomeSteps";
import { SETUP_INTRO_COPY, WELCOME_GUIDE_COPY } from "@fomofi/shared";

export const GUIDE_ROUTES = [
  "/banks",
  "/financial-analysis",
  "/forecast",
  "/dashboard",
  "/debt",
] as const;

/**
 * The heading each destination shows on arrival, for the box's after-setup form (X4):
 * there the cards are navigation, so they are named after the page, not the step.
 * Four match their sidebar label (a test holds them to it); /banks is not in the
 * sidebar, so it carries ContentHeader's "Connected Banks".
 * Named `…COPY` so scripts/copy-inventory.mjs lists these names for review.
 */
const GUIDE_PAGE_COPY = [
  "Connected Banks",
  "Financial Analysis",
  "Forecast",
  "Dashboard",
  "Debt Eliminator",
] as const;

const GUIDE_ICONS: LucideIcon[] = [
  Landmark,
  ClipboardList,
  TrendingUp,
  LayoutDashboard,
  CreditCard,
];

/** With the redesign switch on (RD-08): the Dashboard is Home and Forecast (Allocate) is
 *  retired into Plan (Paul, 2026-10-29), so the after-setup cards name and link the
 *  redesign's items. */
export const REDESIGN_GUIDE_ROUTES = [
  "/banks",
  "/financial-analysis",
  "/plan",
  "/home",
  "/debt",
] as const;

const REDESIGN_GUIDE_PAGE_COPY = [
  "Connected Banks",
  "Financial Analysis",
  "Plan",
  "Home",
  "Debt Eliminator",
] as const;

const REDESIGN_GUIDE_ICONS: LucideIcon[] = [Landmark, ClipboardList, Layers, Home, CreditCard];

export interface GuideCard {
  to: (typeof GUIDE_ROUTES)[number] | (typeof REDESIGN_GUIDE_ROUTES)[number];
  /** The page's own name: what an after-setup card is titled. */
  page: string;
  /** The journey step: what a card inside setup is titled. */
  title: string;
  description: string;
  icon: LucideIcon;
}

export function guideCards(redesign = false): GuideCard[] {
  return WELCOME_STEPS.map((step, index) => ({
    to: (redesign ? REDESIGN_GUIDE_ROUTES : GUIDE_ROUTES)[index],
    page: (redesign ? REDESIGN_GUIDE_PAGE_COPY : GUIDE_PAGE_COPY)[index],
    title: step.title,
    description: step.description,
    icon: (redesign ? REDESIGN_GUIDE_ICONS : GUIDE_ICONS)[index],
  }));
}

/** The box's title and description for each form (rules 4 and 5). Lives here, not in
 *  the dialog, because the Dialog renders through a portal, so a static render
 *  cannot see them. The words themselves are shared with mobile (WELCOME_GUIDE_COPY). */
export function welcomeGuideHeading(inSetup: boolean): { title: string; description: string } {
  return inSetup
    ? { title: WELCOME_GUIDE_COPY.setup.title, description: SETUP_INTRO_COPY }
    : {
        title: WELCOME_GUIDE_COPY.afterSetup.title,
        description: WELCOME_GUIDE_COPY.afterSetup.description,
      };
}
